import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import axios from 'axios';
import { normalizeEmail } from '../utils/normalizeEmail';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8090/api';

// Create a public API instance without auth token interceptor for password reset
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  open,
  onClose,
}) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    const normalizedEmail = normalizeEmail(email);

    // Validation
    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      // Use public API instance (no auth token required for password reset)
      // Note: PocketBase returns 204 even if email doesn't exist (security feature)
      // So we always show success message to prevent email enumeration
      await publicApi.post('/collections/client_users/request-password-reset', {
        email: normalizedEmail,
      });
      setSuccess(true);
      // Clear email after successful submission
      setTimeout(() => {
        setEmail('');
        setSuccess(false);
      }, 5000);
    } catch (err: any) {
      console.error('Error requesting password reset:', err);
      
      // Handle different error response structures from PocketBase
      if (err.response?.data) {
        const errorData = err.response.data;
        
        // Check for field-specific errors (e.g., email field errors)
        if (errorData.data && typeof errorData.data === 'object') {
          // Check if there's an email field error
          if (errorData.data.email) {
            const emailError = errorData.data.email;
            if (typeof emailError === 'object' && emailError.message) {
              setError(emailError.message);
            } else if (typeof emailError === 'string') {
              setError(emailError);
            } else {
              setError('Invalid email address. Please check and try again.');
            }
          } else {
            // Extract all field errors
            const fieldErrors = Object.entries(errorData.data)
              .map(([field, err]: [string, any]) => {
                if (typeof err === 'object' && err.message) {
                  return err.message;
                } else if (typeof err === 'string') {
                  return err;
                }
                return `${field}: Invalid value`;
              })
              .join('. ');
            
            setError(fieldErrors || errorData.message || 'Failed to send password reset email. Please try again.');
          }
        } else if (errorData.message) {
          // Direct error message
          setError(errorData.message);
        } else {
          setError('Failed to send password reset email. Please try again.');
        }
      } else if (err.message) {
        // Network or other errors
        setError(err.message);
      } else {
        setError('Failed to send password reset email. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Reset Password
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              If this email exists in our system, a password reset link has been sent. Please check your inbox.
            </Alert>
          )}

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            autoFocus
            disabled={loading || success}
            placeholder="Enter your email address"
            InputProps={{
              startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || success || !email}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Sending...' : 'Send Reset Link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForgotPasswordModal;
